import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { UpdateFormFn } from '@ur-solutions/react-hooks'
import { ChevronDown, Filter } from 'lucide-react'
import React from 'react'
import { isMobile } from 'react-device-detect'
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@/components/ui'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { cn } from '@/lib/utils'
import { useOnErrorAuto } from '@/utils/hooks'
import { ALL_ISSUE_CATEGORIES_SHALLOW_QUERY } from '../queries'
import { ISSUE_INVOLVEMENT_FILTERS, ISSUE_STATUS_FILTERS } from '../types'
import type {
  IssueInvolvementFilter,
  IssueStatusFilter,
  IssuesTableFilter,
} from '../types'
import type {
  AllIssueCategoriesShallowQuery,
  IssueSeverity,
} from '../types.graphl'
import {
  isCategorySelected,
  toggleCategoryExclusion,
  toggleMultiSelect,
} from './issue-filter-utils'

const ISSUE_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const

interface Props {
  filter: IssuesTableFilter
  onUpdate: UpdateFormFn<IssuesTableFilter>
}

interface PillOption<T extends string> {
  value: T
  label: string
}

/** A row of rounded "pill" toggles laid out in equal-width columns. */
const PillGroup = <T extends string>({
  options,
  isActive,
  onToggle,
}: {
  options: PillOption<T>[]
  isActive: (value: T) => boolean
  onToggle: (value: T) => void
}) => (
  <div
    className="grid gap-2"
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
  >
    {options.map(option => {
      const active = isActive(option.value)
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={active}
          onClick={() => onToggle(option.value)}
          className={cn(
            'rounded-full border px-3 py-2.5 text-xs font-semibold tracking-wide uppercase transition-colors',
            'focus-visible:ring-secondary/50 focus-visible:ring-2 focus-visible:outline-none',
            active
              ? 'border-secondary bg-secondary/10 text-secondary'
              : 'border-transparent bg-quaternary text-tertiary hover:text-primary'
          )}
        >
          {option.label}
        </button>
      )
    })}
  </div>
)

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="space-y-2">
    <h4 className="text-sm font-semibold text-primary">{title}</h4>
    {children}
  </div>
)

export const IssuePopupFilter: React.FC<Props> = ({ filter, onUpdate }) => {
  const translations = useTranslate({
    filter: 'common.filter',
    reset: 'common.reset',
    done: 'common.ok',

    status: 'common.status',
    archived: 'common.archived',
    categories: 'common.categories',
    all: 'common.all',

    open: 'common.open-alt',
    closed: 'common.closed',
    started: 'issues.started',

    severities: 'common.severities',
    low: 'common.low',
    medium: 'common.medium',
    high: 'common.high',

    involvement: 'issues.involvement',
    involved: 'issues.involved',
    notInvolved: 'issues.not-involved',
  })
  const onErrorAuto = useOnErrorAuto()
  const { data: categoriesData, loading: categoriesLoading } =
    useQuery<AllIssueCategoriesShallowQuery>(
      ALL_ISSUE_CATEGORIES_SHALLOW_QUERY,
      {
        onError: onErrorAuto(),
      }
    )

  const categories =
    categoriesData?.allIssueCategories.edges.map(edge => edge.node) ?? []
  const allCategoryIds = categories.map(category => category.id)

  const allStatusesSelected = filter.statuses.length === 0
  const allSeveritiesSelected = filter.severities.length === 0
  const allInvolvementSelected = filter.involvement.length === 0
  const allCategoriesSelected = filter.excludeCategories.length === 0

  const activeCount =
    (filter.statuses.length > 0 ? 1 : 0) +
    (filter.severities.length > 0 ? 1 : 0) +
    (filter.involvement.length > 0 ? 1 : 0) +
    (filter.excludeCategories.length > 0 ? 1 : 0) +
    (filter.archived ? 1 : 0)

  const toggleStatus = (status: IssueStatusFilter) =>
    onUpdate({
      statuses: toggleMultiSelect(
        status,
        filter.statuses,
        ISSUE_STATUS_FILTERS
      ),
    })

  const toggleSeverity = (severity: IssueSeverity) =>
    onUpdate({
      severities: toggleMultiSelect(
        severity,
        filter.severities,
        ISSUE_SEVERITIES
      ),
    })

  const toggleInvolvement = (value: IssueInvolvementFilter) =>
    onUpdate({
      involvement: toggleMultiSelect(
        value,
        filter.involvement,
        ISSUE_INVOLVEMENT_FILTERS
      ),
    })

  const toggleCategory = (id: string) =>
    onUpdate({
      excludeCategories: toggleCategoryExclusion(
        id,
        filter.excludeCategories,
        allCategoryIds
      ),
    })

  const resetAll = () =>
    onUpdate({
      statuses: [],
      severities: [],
      involvement: [],
      excludeCategories: [],
      archived: false,
    })

  const body = (
    <div className="space-y-5">
      <Section title={translations.status}>
        <PillGroup<IssueStatusFilter>
          options={[
            { value: 'OPEN', label: translations.open },
            { value: 'STARTED', label: translations.started },
            { value: 'CLOSED', label: translations.closed },
          ]}
          isActive={value =>
            allStatusesSelected || filter.statuses.includes(value)
          }
          onToggle={toggleStatus}
        />
      </Section>

      <Section title={translations.severities}>
        <PillGroup<IssueSeverity>
          options={[
            { value: 'LOW', label: translations.low },
            { value: 'MEDIUM', label: translations.medium },
            { value: 'HIGH', label: translations.high },
          ]}
          isActive={value =>
            allSeveritiesSelected || filter.severities.includes(value)
          }
          onToggle={toggleSeverity}
        />
      </Section>

      <Section title={translations.archived}>
        <PillGroup<'false' | 'true'>
          options={[
            { value: 'false', label: translations.open },
            { value: 'true', label: translations.archived },
          ]}
          isActive={value => String(filter.archived) === value}
          onToggle={value => onUpdate({ archived: value === 'true' })}
        />
      </Section>

      <Section title={translations.involvement}>
        <PillGroup<IssueInvolvementFilter>
          options={[
            { value: 'INVOLVED', label: translations.involved },
            { value: 'NOT_INVOLVED', label: translations.notInvolved },
          ]}
          isActive={value =>
            allInvolvementSelected || filter.involvement.includes(value)
          }
          onToggle={toggleInvolvement}
        />
      </Section>

      <Section title={translations.categories}>
        {categoriesLoading ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-quaternary">
              <Checkbox
                size="md"
                value={allCategoriesSelected}
                onChange={() => onUpdate({ excludeCategories: [] })}
              />
              <span className="text-sm font-medium text-primary">
                {translations.all}
              </span>
            </label>
            {categories.map(category => (
              <label
                key={category.id}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-quaternary"
              >
                <Checkbox
                  size="md"
                  value={isCategorySelected(
                    category.id,
                    filter.excludeCategories
                  )}
                  onChange={() => toggleCategory(category.id)}
                />
                <span className="text-sm text-primary">{category.name}</span>
              </label>
            ))}
          </div>
        )}
      </Section>
    </div>
  )

  const trigger = (
    <Button
      variant="outline"
      className="h-15.5 w-full justify-between rounded-xl px-4 text-base font-normal text-primary"
    >
      <span className="flex items-center gap-2">
        <Filter className="size-4 text-secondary" />
        {translations.filter}
        {activeCount > 0 && (
          <span className="inline-flex size-5 items-center justify-center rounded-full bg-secondary text-xs font-semibold leading-none text-white tabular-nums">
            {activeCount}
          </span>
        )}
      </span>
      <ChevronDown className="size-4 text-tertiary" />
    </Button>
  )

  if (isMobile) {
    return (
      <Drawer>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[88vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{translations.filter}</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto overscroll-contain px-4 pb-2">
            {body}
          </div>
          <DrawerFooter className="flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              disabled={activeCount === 0}
              onClick={resetAll}
            >
              {translations.reset}
            </Button>
            <DrawerClose asChild>
              <Button className="flex-1">{translations.done}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[22rem] max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Filter className="size-4 text-secondary" />
            {translations.filter}
          </span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={resetAll}
              className="text-xs font-medium text-tertiary hover:text-primary"
            >
              {translations.reset}
            </button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">{body}</div>
      </PopoverContent>
    </Popover>
  )
}
